import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Torii } from "../components/shrine/Torii";
import { Miko } from "../components/miko/Miko";
import { Sakura } from "../components/shrine/Sakura";
import { useSound } from "../hooks/useSound";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";

/**
 * §1.1 Welcome 画面
 *
 * 演出シーケンス (約 4.5秒):
 *  0.0s  黒画面
 *  0.3s  鈴音「シャンッ」+ 軽い振動
 *  0.6s  鳥居がフェード&ズームイン (parallax)
 *  1.8s  鳥居をくぐる (鳥居が外側にスケールしながらフェードアウト)
 *  2.4s  境内 (桜が舞い始める)
 *  2.8s  巫女が画面奥から歩いて来る (下から fade + slide-up)
 *  3.6s  「ようこそ、Musubiへ」 が毛筆風に表示
 *  4.2s  「はじめる」 ボタンが出現
 *
 * iOS の AudioContext 制約: ユーザータップで unlock するため、初回タップでも音が出るように
 * 「タップで始める」 オーバーレイを最初に置く。一度タップ後に上記シーケンスを開始。
 */

type Phase = "idle" | "bell" | "torii" | "passing" | "shrine" | "miko" | "text" | "button";

const reduceMotionPhases: Phase[] = ["button"];

export function Welcome() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();
  const prefersReducedMotion = useReducedMotion();

  const [started, setStarted] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");

  function startSequence() {
    unlock();
    setStarted(true);

    if (prefersReducedMotion) {
      setPhase("button");
      return;
    }

    const schedule: Array<[number, Phase, () => void]> = [
      [0, "bell", () => {
        play("bellShake");
        vibrate(VibrationPatterns.bellTap);
      }],
      [600, "torii", () => {}],
      [1800, "passing", () => {}],
      [2400, "shrine", () => {}],
      [2800, "miko", () => {}],
      [3600, "text", () => {}],
      [4200, "button", () => {
        vibrate(20);
      }],
    ];

    const timers = schedule.map(([delay, p, fn]) =>
      window.setTimeout(() => {
        setPhase(p);
        fn();
      }, delay),
    );

    return () => timers.forEach(clearTimeout);
  }

  useEffect(() => {
    if (prefersReducedMotion && started) {
      setPhase(reduceMotionPhases[0]);
    }
  }, [prefersReducedMotion, started]);

  function handleStart() {
    play("neraOpen");
    vibrate(VibrationPatterns.cardOpen);
    // 軽い遷移演出後に signup へ
    window.setTimeout(() => navigate("/signup"), 350);
  }

  return (
    <main className="relative min-h-dvh overflow-hidden bg-[var(--color-night-deep)]">
      {/* ベース夜空 */}
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 70%, rgba(159,29,53,0.15), transparent 60%), linear-gradient(to bottom, #050203, #0a0608 60%, #1a0c12)",
        }}
      />

      {/* 桜 (境内出現後) */}
      <AnimatePresence>
        {(phase === "shrine" || phase === "miko" || phase === "text" || phase === "button") && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0"
          >
            <Sakura count={16} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* 鳥居 — ズームイン → くぐる */}
      <AnimatePresence>
        {(phase === "torii" || phase === "passing") && (
          <motion.div
            key="torii"
            initial={{ opacity: 0, scale: 0.4, y: 20 }}
            animate={
              phase === "passing"
                ? { opacity: 0, scale: 4.2, y: -40 }
                : { opacity: 1, scale: 1, y: 0 }
            }
            exit={{ opacity: 0, scale: 5 }}
            transition={
              phase === "passing"
                ? { duration: 1.0, ease: [0.4, 0.0, 0.2, 1] }
                : { duration: 1.1, ease: [0.16, 1, 0.3, 1] }
            }
            className="absolute inset-0 flex items-center justify-center"
          >
            <Torii className="w-[68vw] max-w-[420px]" glow />
          </motion.div>
        )}
      </AnimatePresence>

      {/* 巫女 */}
      <AnimatePresence>
        {(phase === "miko" || phase === "text" || phase === "button") && (
          <motion.div
            key="miko"
            initial={{ opacity: 0, y: 60, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.0, ease: "easeOut" }}
            className="absolute inset-x-0 bottom-[20%] flex justify-center"
          >
            <Miko size={220} className="drop-shadow-[0_12px_36px_rgba(159,29,53,0.5)]" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* 「ようこそ、Musubiへ」 — 毛筆 */}
      <AnimatePresence>
        {(phase === "text" || phase === "button") && (
          <motion.h1
            key="title"
            initial={{ opacity: 0, letterSpacing: "0.4em" }}
            animate={{ opacity: 1, letterSpacing: "0.2em" }}
            transition={{ duration: 1.4, ease: "easeOut" }}
            className="absolute top-[14%] inset-x-0 text-center text-[var(--color-washi-warm)] text-3xl"
            style={{
              fontFamily: "var(--font-fude)",
              textShadow: "0 0 24px rgba(201,169,97,0.5), 0 4px 12px rgba(0,0,0,0.6)",
            }}
          >
            {t("welcome.subtitle")}
          </motion.h1>
        )}
      </AnimatePresence>

      {/* 「はじめる」 ボタン */}
      <AnimatePresence>
        {phase === "button" && (
          <motion.div
            key="cta"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="absolute bottom-[8%] inset-x-0 flex flex-col items-center gap-3 px-6"
          >
            <motion.button
              onClick={handleStart}
              whileTap={{ scale: 0.94 }}
              whileHover={{ scale: 1.04 }}
              className="relative px-12 py-4 rounded-full text-[var(--color-washi)] tracking-[0.3em] text-sm font-medium"
              style={{
                background:
                  "linear-gradient(180deg, #c43a55 0%, #9f1d35 50%, #6b1024 100%)",
                border: "1px solid rgba(201,169,97,0.7)",
                boxShadow:
                  "0 0 32px rgba(159,29,53,0.55), inset 0 1px 0 rgba(255,255,255,0.2)",
              }}
            >
              <span style={{ fontFamily: "var(--font-fude)" }}>{t("welcome.start")}</span>
            </motion.button>
            <span className="text-[10px] text-[var(--color-gold-light)] opacity-60">
              {t("welcome.tagline")}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 初回タップオーバーレイ (AudioContext unlock 用) */}
      <AnimatePresence>
        {!started && (
          <motion.button
            key="overlay"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={startSequence}
            className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-[var(--color-night-deep)]"
            aria-label={t("welcome.start")}
          >
            <motion.div
              animate={{
                scale: [1, 1.08, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              className="text-[var(--color-gold)] text-7xl"
              style={{ textShadow: "0 0 32px rgba(201,169,97,0.7)" }}
            >
              ⛩
            </motion.div>
            <p
              className="text-[var(--color-washi-warm)] tracking-[0.3em] text-sm"
              style={{ fontFamily: "var(--font-fude)" }}
            >
              {t("welcome.title")}
            </p>
            <p className="text-[10px] text-[var(--color-gold-light)] opacity-70 tracking-widest">
              TAP TO ENTER
            </p>
          </motion.button>
        )}
      </AnimatePresence>
    </main>
  );
}
